import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { USER_INDEX_MODES } from 'components/apps/RootPages/UserRootPage/constants';
import ScenarioGeneratorCard from 'components/ScenarioGeneratorCard';
import { isEqual } from 'lodash';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';
import { errorAlert } from 'ui-kit/Alert';
import Toggler from 'ui-kit/Toggler';
import { isBlank } from 'utils/storeUtils';

import EmailConfirmationModal from '../../../components/modals/EmailConfirmationModal';
import { CATEGORIES_ORDER, DEFAULT_TAGS } from '../../../constants';
import { ANCHORS } from '../../../constants/anchors.ts';
import { useTags } from '../../../hooks/useTags';
import { useLazyGetGamesLikesMapQuery, useLazyGetMyLikedGamesIdsQuery, useLazyGetPublicGamesQuery } from '../../../services/api/gamesApi';
import { capitalizeFirstLetter } from '../../../utils';
import GameCard from './GameCard';
import ScenarioGapCard from './ScenarioGapCard';
import ScenariosSkeleton from './ScenariosSkeleton';
export const getAllTags = (data) => {
  if (!data?.length) return [];

  const tagsMap = new Map();
  data.forEach(item => {
    item?.tags?.forEach(tag => {
      tagsMap.set(tag, (tagsMap.get(tag) || 0) + 1);
    });
  });

  return new Map([...tagsMap.entries()].sort((a, b) => b[1] - a[1]));
};

export const getCategoriesAndTags = (games = [], tags = []) => {
  if (!games.length || !tags.length) return new Map();

  const tagsFromGames = new Set(games.flatMap(game => game.tags));

  return tags.reduce((categoriesMap, tag) => {
    if (tagsFromGames.has(tag.name)) {
      categoriesMap.set(tag.category, [
        ...(categoriesMap.get(tag.category) || []),
        tag.name,
      ]);
    }
    return categoriesMap;
  }, new Map());
};

const PublicGamesListTool = (props) => {
  const { onAuthClick, onCreateScenario, onItemClick, mode, onToggleMode, isShowCustomScenariosButton } = props;

  const { tags: tagsData } = useTags();
  const [getPublicGames, { isLoading: isGamesLoading }] = useLazyGetPublicGamesQuery();
  const [getGamesLikesMap, { isLoading: isLikesLoading }] = useLazyGetGamesLikesMapQuery();
  const [getMyLikedGamesIds, { isLoading: isLikedGamesLoading }] = useLazyGetMyLikedGamesIdsQuery();

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => ({
      currentUser: state.users.usersMap.get(state.users.currentUserId),
      hasSubscription: state.users.usersMap.get(state.users.currentUserId)?.hasSubscription === true,
    }), [])
  );

  const [publicGames, setPublicGames] = useState([]);
  const [likesMap, setLikesMap] = useState(undefined);
  const [myLikedGamesIds, setMyLikedGamesIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsDB, setTagsDB] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const isPrivateMode = mode === USER_INDEX_MODES.PRIVATE;

  const [allTags, setAllTags] = useState([]);
  const categoriesAndTags = useMemo(() => getCategoriesAndTags(publicGames, tagsDB), [publicGames, tagsDB]);
  const [relatedTags, setRelatedTags] = useState([]);

  const filteredPublicGamesByHasUserEmail = useMemo(() => {
    if (isBlank(publicGames)) {
      return [];
    }
    if (currentUser?.email) {
      return [...publicGames];
    }
    return [...publicGames]?.filter(game => !!game.isFree);
  }, [publicGames, currentUser]);

  const sortedPublicGames = useMemo(() => {
    return filteredPublicGamesByHasUserEmail.filter(item => selectedTags.length === 0 || selectedTags.every(tag => item.tags?.includes(tag)));
  }, [publicGames, selectedTags]);



  useEffect(() => {
    if (selectedTags?.length === 0) {
      setRelatedTags(allTags);
    } else {
      const tags = Array.from(getAllTags(sortedPublicGames)?.keys());
      setRelatedTags(tags);
    }
  }, [selectedTags]);

  const handleTagClick = useCallback((tag) => {
    setSelectedTags(prevTags =>
      prevTags.includes(tag) ? prevTags.filter(x => x !== tag) : [...prevTags, tag]
    );
  }, []);

  const renderTags = (tags) => (
    tags.map(tag => {
      const isSelected = selectedTags.includes(tag);
      const isRelated = relatedTags.includes(tag);
      return (
        <Tag
          key={tag}
          onClick={() => handleTagClick(tag)}
          selected={isSelected}
          related={isSelected || isRelated}
        >
          {tag}
        </Tag>
      );
    })
  );

  const handleClearFilters = useCallback(() => setSelectedTags([]), []);

  const handleCreateScenario = useCallback(() => {
    if (!currentUser) {
      onAuthClick();
    } else if (!currentUser?.emailVerified) {
      setIsEmailConfirmationModalOpen(true);
    } else {
      onCreateScenario(selectedTags);
    }
  }, [currentUser, onAuthClick, onCreateScenario, selectedTags]);

  const renderEmailConfirmationModal = () => (
    isEmailConfirmationModalOpen && (
      <EmailConfirmationModal
        isOpen={isEmailConfirmationModalOpen}
        onClose={() => setIsEmailConfirmationModalOpen(false)}
      />
    )
  );

  const shouldShowLikes = likesMap !== undefined;
  const shouldShowMobileToggler = isMobile && isShowCustomScenariosButton;


  useEffect(() => {
    if (tagsData && !isEqual(tagsData, tagsDB)) {
      setTagsDB(tagsData);
    }
  }, [tagsData, tagsDB]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitialLoading(true);
        const [gamesResponse, likesResponse] = await Promise.all([
          getPublicGames(),
          getGamesLikesMap()
        ]);

        setPublicGames(gamesResponse.data);
        setLikesMap(likesResponse.data);

        const tags = Array.from(getAllTags(gamesResponse.data)?.keys());
        setAllTags(tags);
        setRelatedTags(tags);

        if (currentUser) {
          const { data: myLikedGamesIds } = await getMyLikedGamesIds();
          setMyLikedGamesIds(myLikedGamesIds);
        }
      } catch (error) {
        errorAlert('Failed to load scenarios');
        throw error;
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const isLoading = isInitialLoading || isGamesLoading || isLikesLoading || (currentUser && isLikedGamesLoading);

  if (isLoading) {
    return <ScenariosSkeleton showTagsSkeleton={true} />;
  }

  return (
    <div>
      <div style={{ height: 0, width: 0 }} id={ANCHORS.FEATURED_SCENARIOS}></div>
      {shouldShowMobileToggler && <Toggler label='My Scenarios' isActive={isPrivateMode} onToggle={onToggleMode} />}
      <div className="flex flex-row flex-wrap items-center gap-2 pt-3 pb-2 text-white">
        {!isMobile && isShowCustomScenariosButton && (
          <TogglerWrapper>
            <Toggler label='My Scenarios' isActive={isPrivateMode} onToggle={onToggleMode} />
          </TogglerWrapper>
        )}
        <Tag onClick={handleClearFilters} selected={selectedTags.length === 0} related={true}>
          All
        </Tag>
        {showAllTags && <div className="w-full" />}
        {showAllTags
          ? CATEGORIES_ORDER.map(category => {
            const tags = categoriesAndTags.get(category);
            if (!tags) return null;
            return (
              <div key={category} className="flex w-full flex-row flex-wrap items-center gap-2">
                <div className="font-bold" style={{ fontSize: 16, padding: '6px 8px' }}>
                  {capitalizeFirstLetter(category)}:
                </div>
                {renderTags(tags)}
              </div>
            );
          })
          : renderTags([...DEFAULT_TAGS, ...(categoriesAndTags.get('fantasy') || [])].slice(0, 5), (tag) => selectedTags.includes(tag))}
        <MoreTags onClick={() => setShowAllTags(!showAllTags)}>
          {showAllTags ? 'Hide' : 'Show more'}
        </MoreTags>
      </div>

      {sortedPublicGames.length === 0 ? (
        <NoScenarios>
          <span>No scenarios</span>
          <ClearFiltersButton onClick={handleClearFilters}>Clear filters</ClearFiltersButton>
        </NoScenarios>
      ) : (
        <div className="mx-auto box-border py-4">
          <div className="box-border grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
            {sortedPublicGames.map((item, index) => {
              if (item.isCustomScenarioGap) {
                return <ScenarioGapCard onCreateScenario={onCreateScenario} key={`gap-${index}`} item={item} />;
              }

              const isLikedByMe = myLikedGamesIds.includes(item.id);
              const usersLikes = likesMap?.[item.id] || 0;
              const defaultLikesNumber = item?.likesNumber || 672;
              const likesToShow = defaultLikesNumber + usersLikes;
              const isLiking = item?.id === undefined;

              return (
                <GameCard
                  key={`${index}-${item.id}`}
                  item={item}
                  onItemClick={onItemClick}
                  isLikedByMe={isLikedByMe}
                  usersLikes={usersLikes}
                  defaultLikesNumber={defaultLikesNumber}
                  likesToShow={likesToShow}
                  isLiking={isLiking}
                  shouldShowLikes={shouldShowLikes}
                  hasSubscription={hasSubscription}
                />
              );
            })}
          </div>
        </div>
      )}
      <ScenarioGeneratorCard onButtonClick={handleCreateScenario} />
      {renderEmailConfirmationModal()}
    </div>
  );
};

export default PublicGamesListTool;

const Tag = styled.div`
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
  color: ${props => (props?.selected ? 'white' : 'rgba(169, 169, 169, 1)')};
  background-color: ${props => (props?.selected ? 'rgba(255, 126, 85, 1)' : 'rgba(29, 29, 29, 1)')};
  opacity: ${props => (props?.related ? '1' : '0.4')};
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.1s;
  ::first-letter {
    text-transform: uppercase;
  }
`;

const MoreTags = styled.div`
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
  color: white;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
`;

const NoScenarios = styled.div`
  font-family: Nunito Sans;
  font-size: 24px;
  font-weight: 700;
  line-height: 26px;
  color: white;
  text-align: center;
  padding-top: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ClearFiltersButton = styled.button`
  background-color: rgba(255, 126, 85, 1);
  width: fit-content;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 18px;
  line-height: 20px;
  font-weight: bold;
`;

const TogglerWrapper = styled.div`
  margin-right: 24px;
`;
